import { useEffect, useRef } from 'react';

import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { usePdpDeliveryOptions } from '@/data/Content/PdpDeliveryOptions';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';

import { TrackItemDeliveryPDPProps } from './types';
import { useTrackItemDeliveryPDPGA4 } from './useTrackItemDeliveryPDPGA4';
import { getDeliveryDays, isLeadTimeStaleForStore } from './utils';

export const useTrackItemDeliveryPDP = ({
	product,
	isStoreOnlyAvailable = false,
}: TrackItemDeliveryPDPProps) => {
	const { pickup, delivery, isInventoryLoading } = usePdpDeliveryOptions(
		isStoreOnlyAvailable ? undefined : product
	);
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { zipCode } = useDeliveryLocationCookie();
	const sendGA4Event = useTrackItemDeliveryPDPGA4();

	const trackedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!product || !physicalStoreId) return;
		if (!isStoreOnlyAvailable) {
			if (isInventoryLoading) return;
			if (pickup.status === 'pending' || delivery.status === 'pending') return;
			if (isLeadTimeStaleForStore(product, physicalStoreId)) return;
		}

		const itemId = getItemPartNumber(product);
		const trackingKey = `${itemId}|${physicalStoreId}|${zipCode}`;
		if (trackedKeyRef.current === trackingKey) return;
		trackedKeyRef.current = trackingKey;

		sendGA4Event({ itemId, ...getDeliveryDays(isStoreOnlyAvailable, { delivery, pickup }) });
	}, [
		product,
		physicalStoreId,
		zipCode,
		isStoreOnlyAvailable,
		isInventoryLoading,
		pickup,
		delivery,
		sendGA4Event,
	]);
};
